import { FileText, Image as ImageIcon, Loader2, X } from "lucide-react"

import { useI18n } from "@/i18n"
import type { FileAttachment } from "@/types/api"

export type UploadState =
  | { status: "idle" }
  | { status: "uploading"; filename: string }
  | { status: "ready"; attachment: FileAttachment }
  | { status: "error"; message: string }

export function AttachmentChip({ state, onRemove }: { state: UploadState; onRemove: () => void }) {
  const { t } = useI18n()
  if (state.status === "idle") return null

  if (state.status === "uploading") {
    return (
      <span className="inline-flex max-w-60 items-center gap-1.5 self-start rounded-full border bg-muted px-3 py-1 text-xs text-muted-foreground">
        <Loader2 className="size-3 shrink-0 animate-spin" />
        <span className="truncate">{t("attachment.uploading", { filename: state.filename })}</span>
      </span>
    )
  }

  if (state.status === "error") {
    return (
      <span className="inline-flex max-w-72 items-center gap-1.5 self-start rounded-full border border-destructive/40 bg-destructive/10 px-3 py-1 text-xs text-destructive">
        <span className="truncate">⚠️ {state.message}</span>
        <button type="button" onClick={onRemove} className="shrink-0 hover:opacity-70" aria-label={t("attachment.dismiss")}>
          <X className="size-3" />
        </button>
      </span>
    )
  }

  const { attachment } = state
  return (
    <span className="inline-flex max-w-60 items-center gap-1.5 self-start rounded-full border border-primary/25 bg-primary/5 px-3 py-1 text-xs text-primary">
      {attachment.type === "image" ? <ImageIcon className="size-3 shrink-0" /> : <FileText className="size-3 shrink-0" />}
      <span className="truncate">{attachment.filename}</span>
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 text-primary/60 hover:text-primary"
        aria-label={t("attachment.remove")}
      >
        <X className="size-3" />
      </button>
    </span>
  )
}
